import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck, Brain, Radar, PhoneOff, FileCheck2, BarChart3, Trophy, CheckCircle, ArrowRight, Loader2, Sparkles,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { DashboardReplica } from "../components/CallIntellShowcase";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  { icon: Brain, title: "AI Fraud Detection", desc: "Every call scored for bots, spoofed numbers, and repeat callers." },
  { icon: Radar, title: "Real-Time Monitoring", desc: "24/7 live view of every publisher, DID, and campaign." },
  { icon: PhoneOff, title: "Spam & IVR Filtering", desc: "Robocalls, junk, and IVR traffic stopped automatically." },
  { icon: FileCheck2, title: "Call Quality Verification", desc: "Intent, duration, and accent checks on every recording." },
  { icon: Trophy, title: "Publisher Ranking", desc: "See which traffic sources deliver your best-converting calls." },
  { icon: BarChart3, title: "Buyer Optimizer", desc: "Revenue insights that show where to scale and where to cut." },
];

const PLANS = [
  {
    name: "Starter", price: "$299", per: "/month", minutes: "10,000 minutes / month",
    perks: ["AI fraud & spam detection", "Call quality verification", "Real-time call dashboard", "Publisher reports", "Email support"],
  },
  {
    name: "Growth", price: "$599", per: "/month", minutes: "25,000 minutes / month", popular: true,
    perks: ["Everything in Starter", "Publisher ranking & exclusions", "DID status monitoring", "Action Center alerts", "Priority support"],
  },
  {
    name: "Enterprise", price: "Custom", per: "", minutes: "Unlimited minutes",
    perks: ["Everything in Growth", "Buyer Optimizer", "Custom AI rules & blacklists", "API access & integrations", "Dedicated account manager"],
  },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const scrollToForm = (plan, setPlan) => {
  if (plan) setPlan(plan);
  document.getElementById("demo-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Hero = ({ setPlan }) => (
  <section className="pt-32 pb-14 sm:pt-40 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-950" data-testid="ci-hero">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
        <span className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-1.5 text-blue-300 text-xs font-semibold uppercase tracking-wider">
          <ShieldCheck className="w-4 h-4" /> By Digital Master Media LLC
        </span>
        <h1 className="mt-5 font-['Poppins'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white" data-testid="ci-title">
          CallIntell<span className="text-blue-400">™</span>{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">AI Call Intelligence</span>
        </h1>
        <p className="mt-5 text-slate-300 text-lg leading-relaxed">
          The same AI platform we use to check and verify every call on our network — now available for your pay-per-call business. Stop paying for fake, spam, and robot calls.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <button onClick={() => scrollToForm(null, setPlan)} data-testid="ci-hero-demo-btn" className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/25 hover:-translate-y-0.5 transition-transform">
            Book a Demo <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })} data-testid="ci-hero-pricing-btn" className="inline-flex items-center justify-center border-2 border-slate-600 text-slate-200 hover:border-blue-400 hover:text-white font-semibold px-8 py-4 rounded-xl transition-colors">
            View Pricing
          </button>
        </div>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="mt-14">
        <DashboardReplica />
      </motion.div>
    </div>
  </section>
);

const Features = () => (
  <section className="py-16 sm:py-20 border-t border-slate-800/70" data-testid="ci-features">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-400">What You Get</p>
      <h2 className="mt-3 font-['Poppins'] text-3xl sm:text-4xl font-bold text-white">Everything You Need to Protect Your Calls</h2>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {FEATURES.map((f) => (
          <div key={f.title} className="rounded-2xl border border-slate-700/60 bg-slate-800/35 backdrop-blur-xl p-6 hover:border-blue-500/50 hover:-translate-y-1 transition-[transform,border-color] duration-300" data-testid={`ci-feature-${slug(f.title)}`}>
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
              <f.icon className="w-5 h-5 text-white" />
            </div>
            <h3 className="mt-4 font-semibold text-white">{f.title}</h3>
            <p className="mt-1.5 text-sm text-slate-400">{f.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PlanCard = ({ p, onSelect }) => (
  <div className={`relative rounded-3xl p-7 flex flex-col ${p.popular ? "bg-gradient-to-b from-blue-600/20 to-purple-600/10 border-2 border-blue-500/60 shadow-[0_0_60px_-20px_rgba(59,130,246,0.6)]" : "bg-slate-800/40 border border-slate-700/70"}`} data-testid={`ci-plan-${slug(p.name)}`}>
    {p.popular && (
      <span className="absolute -top-3 left-7 inline-flex items-center gap-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
        <Sparkles className="w-3 h-3" /> Most Popular
      </span>
    )}
    <h3 className="font-['Poppins'] text-xl font-bold text-white">{p.name}</h3>
    <p className="mt-4">
      <span className="font-['Poppins'] text-4xl font-extrabold text-white">{p.price}</span>
      <span className="text-slate-400 text-sm">{p.per}</span>
    </p>
    <p className="mt-2 text-sm font-semibold text-blue-300">{p.minutes}</p>
    <ul className="mt-6 space-y-3 flex-1">
      {p.perks.map((k) => (
        <li key={k} className="flex items-start gap-2.5 text-sm text-slate-300">
          <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /> {k}
        </li>
      ))}
    </ul>
    <button onClick={() => onSelect(p.name)} data-testid={`ci-plan-${slug(p.name)}-btn`} className={`mt-8 w-full inline-flex items-center justify-center gap-2 font-semibold py-3 rounded-xl transition-colors ${p.popular ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white" : "border-2 border-slate-600 text-slate-200 hover:border-blue-400 hover:text-white"}`}>
      {p.price === "Custom" ? "Contact Sales" : "Book a Demo"} <ArrowRight className="w-4 h-4" />
    </button>
  </div>
);

const Pricing = ({ setPlan }) => (
  <section id="pricing" className="py-16 sm:py-20 border-t border-slate-800/70 bg-slate-900/40" data-testid="ci-pricing">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-2xl mx-auto">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-400">Pricing</p>
        <h2 className="mt-3 font-['Poppins'] text-3xl sm:text-4xl font-bold text-white">Simple Monthly Plans</h2>
        <p className="mt-3 text-slate-400">Rent CallIntell™ monthly. No setup fees, cancel anytime.</p>
      </div>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        {PLANS.map((p) => <PlanCard key={p.name} p={p} onSelect={(name) => scrollToForm(name, setPlan)} />)}
      </div>
    </div>
  </section>
);

const inputCls = "w-full bg-slate-900/70 border border-slate-700 rounded-lg px-4 py-3 text-sm text-slate-100 placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors";

const DemoForm = ({ form, setForm }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API_URL}/api/callintell-demo`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      if (!res.ok) throw new Error();
      setSubmitted(true);
    } catch { setError("Something went wrong. Please check your details and try again."); } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="text-center py-12" data-testid="ci-demo-success">
        <CheckCircle className="w-14 h-14 text-emerald-400 mx-auto" />
        <h3 className="mt-4 font-['Poppins'] text-2xl font-bold text-white">Request Received!</h3>
        <p className="mt-2 text-slate-400">Our manager will contact you shortly to schedule your CallIntell™ demo.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="ci-demo-form">
      <input required className={inputCls} placeholder="Full Name *" value={form.full_name} onChange={set("full_name")} data-testid="ci-input-name" />
      <input className={inputCls} placeholder="Company Name" value={form.company} onChange={set("company")} data-testid="ci-input-company" />
      <input required type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={set("email")} data-testid="ci-input-email" />
      <input required className={inputCls} placeholder="Phone *" value={form.phone} onChange={set("phone")} data-testid="ci-input-phone" />
      <select className={inputCls} value={form.plan} onChange={set("plan")} data-testid="ci-select-plan">
        <option value="">Plan of interest</option>
        {PLANS.map((p) => <option key={p.name} value={p.name}>{p.name} — {p.price}{p.per}</option>)}
      </select>
      <select className={inputCls} value={form.monthly_calls} onChange={set("monthly_calls")} data-testid="ci-select-volume">
        <option value="">Monthly call volume</option>
        {["Under 5,000", "5,000 – 25,000", "25,000 – 100,000", "100,000+"].map((v) => <option key={v}>{v}</option>)}
      </select>
      <textarea rows={4} className={`${inputCls} sm:col-span-2`} placeholder="Tell us about your campaigns (optional)" value={form.message} onChange={set("message")} data-testid="ci-input-message" />
      {error && <p className="sm:col-span-2 text-sm text-red-400" data-testid="ci-demo-error">{error}</p>}
      <button type="submit" disabled={loading} data-testid="ci-demo-submit" className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 disabled:opacity-60 text-white font-semibold py-4 rounded-xl transition-colors">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Request a Demo <ArrowRight className="w-4 h-4" /></>}
      </button>
    </form>
  );
};

const DemoSection = ({ form, setForm }) => (
  <section id="demo-form" className="py-16 sm:py-24 border-t border-slate-800/70" data-testid="ci-demo-section">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-10">
      <div className="lg:col-span-2">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-400">Book a Demo</p>
        <h2 className="mt-3 font-['Poppins'] text-3xl sm:text-4xl font-bold text-white">See CallIntell™ in Action</h2>
        <p className="mt-4 text-slate-400 leading-relaxed">Fill in the form and our manager will reach out to schedule a live walkthrough and help you pick the right plan.</p>
        <ul className="mt-6 space-y-3">
          {["Live walkthrough with our manager", "Review of your current call quality", "Plan recommendation for your volume"].map((t) => (
            <li key={t} className="flex items-center gap-2.5 text-sm text-slate-300"><CheckCircle className="w-4 h-4 text-emerald-400" /> {t}</li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-3 rounded-3xl border border-slate-700/70 bg-slate-800/40 backdrop-blur-xl p-6 sm:p-8">
        <DemoForm form={form} setForm={setForm} />
      </div>
    </div>
  </section>
);

export default function CallIntellPage() {
  const [form, setForm] = useState({ full_name: "", company: "", email: "", phone: "", plan: "", monthly_calls: "", message: "" });
  const setPlan = (plan) => setForm((f) => ({ ...f, plan }));
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-['Inter',-apple-system,sans-serif] antialiased" data-testid="callintell-page">
      <Header />
      <Hero setPlan={setPlan} />
      <Features />
      <Pricing setPlan={setPlan} />
      <DemoSection form={form} setForm={setForm} />
      <Footer />
    </div>
  );
}
